import { Component, OnInit, OnDestroy, Inject, Input } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Logger } from '@utils/logger';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';
import { SimpleModalConfig } from '@frk/eds-components';
import {
  DocumentsData,
  GridRowData,
  ProductsUserData,
  SubscribedProductDTO,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { DOCUMENT } from '@angular/common';
import { switchMap, takeUntil } from 'rxjs/operators';
import { SubscriptionsService } from '@marketing/subscriptions-manage/services/subscriptions.service';

/**
 * Logger
 */
const logger = Logger.getLogger('ProductsModalComponent');

@Component({
  selector: 'ft-subscriptions-products-iii-modal',
  templateUrl: './subscriptions-products-iii-modal.component.html',
  styleUrls: ['./subscriptions-products-iii-modal.component.scss'],
})
export class SubscriptionsProductsIiiModalComponent
  implements OnInit, OnDestroy {
  private singleRowData: GridRowData;
  private documentsData: Array<DocumentsData>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public modalConfig: SimpleModalConfig;
  public isVisible$: Observable<boolean>;
  public isloading = false;

  @Input() apiUrl: string;

  constructor(
    private subscriptionProductsService: SubscriptionProductsService,
    private subscriptionsService: SubscriptionsService,
    @Inject(DOCUMENT) readonly documentRef: Document
  ) {
    this.modalConfig = {
      id: 'subscriptionsProductsIiiModal',
    };
  }

  ngOnInit(): void {
    this.isVisible$ = this.subscriptionProductsService.getIsProductsModalVisible$();
    logger.debug('ModalComponent initialised');
    this.subscriptionProductsService
      .getExpandedRowDocumentsData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((documentData) => {
        this.documentsData = documentData;
      });
    this.subscriptionProductsService
      .getSingleRowData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((rowData) => {
        this.singleRowData = rowData;
      });
  }

  /**
   * Close modal window
   */
  public onModalClose() {
    this.documentRef.body.style.overflow = 'auto';
    this.subscriptionProductsService.setIsProductsModalVisible$(false);
    this.singleRowData.isExpanded = false;
    this.singleRowData.isChecked = false;
    this.singleRowData.allActiveDocuments = [];
    this.subscriptionProductsService.updateRowData(this.singleRowData);
  }

  /**
   * Update documents selection in the table view
   */
  public updateDocs(): void {
    const submitDocs: SubscribedProductDTO[] = this.subscriptionProductsService.getSubmittedDocumentsData(
      this.singleRowData,
      this.documentsData
    );
    const userData = this.subscriptionProductsService.getUserData();
    const url =
      this.apiUrl + 'productSubscriptions/globalId/' + userData.globalId;
    this.isloading = true;
    this.subscriptionsService
      .getApiPutRequest$(url, submitDocs)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => {
          return this.subscriptionsService.getApiGetRequest$(
            this.apiUrl + 'productSubscriptions/globalId/' + userData.globalId
          );
        })
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: ProductsUserData) => {
          this.isloading = false;
          this.subscriptionProductsService.setUserData(data);
          logger.debug('Products saved', data);
          this.singleRowData.documentTypes = this.singleRowData.allActiveDocuments
            .map((doc) => doc.documentType)
            .join(' , ');
          this.singleRowData.selectedDocuments = [...this.documentsData];
          this.documentRef.body.style.overflow = 'auto';
          this.subscriptionProductsService.setIsProductsModalVisible$(false);
          this.singleRowData.isExpanded = false;
          this.singleRowData.isChecked = false;
          this.subscriptionProductsService.setSingleRowData$(
            this.singleRowData
          );
          this.subscriptionProductsService.updateRowData(this.singleRowData);
        },
        (error) => {
          logger.debug('Products save error', error);
        }
      );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
